import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import Toast from "./Toast";
import { setToastInstance } from "./index";

const ToastContext = createContext(null);

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback(
    (message, variant = "info", duration = 5000) => {
      const id = toastId++;
      const newToast = {
        id,
        message,
        variant,
        duration,
        open: true,
      };

      setToasts((prev) => [...prev, newToast]);

      return id;
    },
    [],
  );

  const hideToast = useCallback((id) => {
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, open: false } : toast,
      ),
    );

    // Remove from state after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 300);
  }, []);

  const success = useCallback(
    (message, options) => showToast(message, "success", options?.autoClose),
    [showToast],
  );

  const error = useCallback(
    (message, options) => showToast(message, "error", options?.autoClose),
    [showToast],
  );

  const warning = useCallback(
    (message, options) => showToast(message, "warning", options?.autoClose),
    [showToast],
  );

  const info = useCallback(
    (message, options) => showToast(message, "info", options?.autoClose),
    [showToast],
  );

  // Set up global toast instance
  useEffect(() => {
    setToastInstance({ success, error, warning, info });
  }, [success, error, warning, info]);

  return (
    <ToastContext.Provider value={{ success, error, warning, info }}>
      {children}
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          toast={{ ...toast, vertical: "top", horizontal: "right" }}
          onClose={() => hideToast(toast.id)}
          style={{
            top: `${80 + index * 80}px`,
          }}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
